/**
 * TenantsList Component
 *
 * Admin panel page for listing and filtering tenant deployments.
 * Part of the admin dashboard for monitoring multi-tenant CloudFront deployments.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

interface TenantSummary {
  tenantId: string;
  customDomain: string | null;
  deploymentStatus: string;
  certificateStatus: string | null;
  stackStatus: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  photographer: {
    email: string | null;
    businessName: string | null;
  };
}

interface TenantsResponse {
  items: TenantSummary[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  DEPLOYING: 'bg-blue-100 text-blue-800',
  PENDING_VALIDATION: 'bg-yellow-100 text-yellow-800',
  FAILED: 'bg-red-100 text-red-800',
  NOT_STARTED: 'bg-gray-100 text-gray-800',
};

const STATUS_LABELS: Record<string, string> = {
  ACTIVE: 'Active',
  DEPLOYING: 'Deploying',
  PENDING_VALIDATION: 'Pending Validation',
  FAILED: 'Failed',
  NOT_STARTED: 'Not Started',
};

export const TenantsList: React.FC = () => {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const navigate = useNavigate();

  const loadTenants = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number> = {
        page,
        limit: 50,
      };

      if (filter) {
        params.status = filter;
      }

      if (search) {
        params.search = search;
      }

      const response = await api.get<{ data: TenantsResponse }>('/admin/tenants', {
        params,
      });

      setTenants(response.data.data.items);
      setTotalPages(response.data.data.totalPages);
      setTotal(response.data.data.total);
    } catch {
      setError('Failed to load tenants. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filter, search, page]);

  useEffect(() => {
    loadTenants();
  }, [loadTenants]);

  const handleSearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const handleFilterChange = (newFilter: string) => {
    setPage(1);
    setFilter(newFilter);
  };

  const handleRetry = async (tenantId: string) => {
    try {
      await api.post(`/admin/tenants/${tenantId}/retry`);
      loadTenants();
    } catch {
      alert('Failed to retry deployment');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tenant Deployments</h1>
            <p className="text-gray-500 mt-1">
              Manage and monitor photographer custom domain deployments
            </p>
          </div>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            View Dashboard
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <select
              value={filter}
              onChange={(e) => handleFilterChange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">All Statuses</option>
              <option value="PENDING_VALIDATION">Pending Validation</option>
              <option value="DEPLOYING">Deploying</option>
              <option value="ACTIVE">Active</option>
              <option value="FAILED">Failed</option>
            </select>

            <div className="flex-1 flex gap-2">
              <input
                type="text"
                placeholder="Search by domain or tenant ID..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Search
              </button>
            </div>

            <button
              onClick={loadTenants}
              className="px-4 py-2 text-blue-600 hover:text-blue-800"
            >
              Refresh
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading tenants...</p>
          </div>
        )}

        {/* Tenants Table */}
        {!loading && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenant
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Domain
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Certificate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Updated
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                        No tenants found
                      </td>
                    </tr>
                  ) : (
                    tenants.map((tenant) => (
                      <tr
                        key={tenant.tenantId}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/admin/tenants/${tenant.tenantId}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {tenant.photographer?.businessName || 'N/A'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {tenant.tenantId}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {tenant.customDomain || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              STATUS_COLORS[tenant.deploymentStatus] ||
                              STATUS_COLORS.NOT_STARTED
                            }`}
                          >
                            {STATUS_LABELS[tenant.deploymentStatus] ||
                              tenant.deploymentStatus}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="text-sm text-gray-500">
                            {tenant.certificateStatus || '-'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(tenant.updatedAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/tenants/${tenant.tenantId}`);
                            }}
                            className="text-blue-600 hover:text-blue-900 mr-4"
                          >
                            View
                          </button>
                          {tenant.deploymentStatus === 'FAILED' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRetry(tenant.tenantId);
                              }}
                              className="text-orange-600 hover:text-orange-900"
                            >
                              Retry
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-gray-50 px-6 py-3 border-t flex items-center justify-between">
                <div className="text-sm text-gray-500">
                  Showing {tenants.length} of {total} tenants
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantsList;
